import {
    validateBooleanParam,
    validateDateConstructorParam,
    validateUUIDParam,
    validateAllParams,
    validateUUIDArrayParam
} from '../utils';
import type { ApiParamValidatorMap } from '../utils/types';
import type { StartSchedulerArgs } from './types';
import {
    object as yupObject,
    array as yupArray,
    string as yupString
} from 'yup';

/**
 * Possible errors for /tasks API
 */
const REQUIRED_SPLIT_TASK_PAYLOAD_LENGTH = 2;

export enum TasksApiErrors {
    MAX_SORT_FIELDS = 'You can only sort a maximum of 5 fields',
    INCOMPATIBLE_SORT_EXTENT_FIELDS = 'Invalid parameters, extent cannot be used with sort',
    MISSING_DRIVER_ID = 'Missing `driverId` parameter',
    MISSING_DATE = 'Missing `date` parameter',
    MISSING_START_DATE = 'Missing `startDate` parameter',
    MISSING_END_DATE = 'Missing `endDate` parameter',
    MISSING_TASK_ID = 'Missing `taskId` parameter',
    MISSING_TASKIDS = 'Missing `taskIds` parameter',
    MISSING_SPLIT_TASK_PAYLOAD = 'Missing `payload` parameter',
    MISSING_INVOICE_IDS = 'Missing `invoiceIds` parameter',
    MISSING_INVENTORY_ITEM_IDS = 'Missing `inventoryItemUuids` parameter',
    INVALID_DRIVER_ID = 'Invalid `driverId` parameter, must be a UUID string',
    INVALID_DATE = 'Invalid `date` parameter, must be ISO-formatted string (eg yyyy-mm-dd)',
    INVALID_START_DATE = 'Invalid `startDate` parameter, must be ISO-formatted string (eg yyyy-mm-dd)',
    INVALID_END_DATE = 'Invalid `endDate` parameter, must be ISO-formatted string (eg yyyy-mm-dd)',
    INVALID_TASK_ID = 'Invalid `taskId` parameter, must be a UUID string',
    INVALID_TASKIDS = 'Invalid `taskIds` parameter, must be an array of UUID strings',
    INVALID_SPLIT_BY_INVOICE_PAYLOAD = 'Invalid `payload` parameter, must be an array of `two` split task by invoice objects',
    INVALID_SPLIT_BY_INVENTORY_PAYLOAD = 'Invalid `payload` parameter, must be an array of `two` split task by inventory objects',
    INVALID_INVOICE_IDS = 'Invalid `invoiceIds` parameter, must be an array of UUID strings',
    INVALID_INVENTORY_ITEM_IDS = 'Invalid `inventoryItemUuids` parameter, must be an array of UUID strings',
    FAILED_TO_UNASSIGN = 'Failed to unassign tasks before reassignment',
    INVALID_DATE_CONSTRUCTOR = 'Invalid `date` parameter, must be either a string, number, or javascript Date object',
    INVALID_RERUN = 'Invalid `rerun` parameter, must be a boolean value',
    INVALID_CREATE_NEW_ROUTES = 'Invalid `createNewRoutes` parameter, must be a boolean value',
    INVALID_ZONE_SET_ID = 'Invalid `zoneSetId` parameter, must be a UUID string'
}

const startSchedulerValidationCallbacks: ApiParamValidatorMap<StartSchedulerArgs> =
    {
        date: {
            validator: validateDateConstructorParam,
            missingMessage: TasksApiErrors.MISSING_DATE,
            invalidMessage: TasksApiErrors.INVALID_DATE_CONSTRUCTOR
        },
        rerun: {
            validator: validateBooleanParam,
            optional: true,
            invalidMessage: TasksApiErrors.INVALID_RERUN
        },
        createNewRoutes: {
            validator: validateBooleanParam,
            optional: true,
            invalidMessage: TasksApiErrors.INVALID_CREATE_NEW_ROUTES
        },
        taskIds: {
            validator: validateUUIDArrayParam,
            optional: true,
            invalidMessage: TasksApiErrors.INVALID_TASKIDS
        },
        zoneSetId: {
            validator: validateUUIDParam,
            optional: true,
            invalidMessage: TasksApiErrors.INVALID_ZONE_SET_ID
        }
    };

export const getStartSchedulerParamErrors = (params: StartSchedulerArgs) => {
    return validateAllParams(params, startSchedulerValidationCallbacks);
};

const invoicePayloadSchema = yupObject({
    invoiceIds: yupArray()
        .required(TasksApiErrors.MISSING_INVOICE_IDS)
        .typeError(TasksApiErrors.INVALID_INVOICE_IDS)
        .of(
            yupString()
                .required(TasksApiErrors.MISSING_INVOICE_IDS)
                .uuid(TasksApiErrors.INVALID_INVOICE_IDS)
        )
        .min(1, TasksApiErrors.INVALID_INVOICE_IDS)
});

export const splitTaskByInvoiceSchema = yupObject({
    taskId: yupString()
        .required(TasksApiErrors.MISSING_TASK_ID)
        .uuid(TasksApiErrors.INVALID_TASK_ID),
    payload: yupArray()
        .required(TasksApiErrors.MISSING_SPLIT_TASK_PAYLOAD)
        .typeError(TasksApiErrors.INVALID_SPLIT_BY_INVOICE_PAYLOAD)
        .of(invoicePayloadSchema)
        .min(
            REQUIRED_SPLIT_TASK_PAYLOAD_LENGTH,
            TasksApiErrors.INVALID_SPLIT_BY_INVOICE_PAYLOAD
        )
        .max(
            REQUIRED_SPLIT_TASK_PAYLOAD_LENGTH,
            TasksApiErrors.INVALID_SPLIT_BY_INVOICE_PAYLOAD
        )
});

const inventoryPayloadSchema = yupObject({
    inventoryItemUuids: yupArray()
        .required(TasksApiErrors.MISSING_INVENTORY_ITEM_IDS)
        .typeError(TasksApiErrors.INVALID_INVENTORY_ITEM_IDS)
        .of(
            yupString()
                .required(TasksApiErrors.MISSING_INVENTORY_ITEM_IDS)
                .uuid(TasksApiErrors.INVALID_INVENTORY_ITEM_IDS)
        )
        .min(1, TasksApiErrors.INVALID_INVENTORY_ITEM_IDS)
});

export const splitTaskByInventorySchema = yupObject({
    taskId: yupString()
        .required(TasksApiErrors.MISSING_TASK_ID)
        .uuid(TasksApiErrors.INVALID_TASK_ID),
    payload: yupArray()
        .required(TasksApiErrors.MISSING_SPLIT_TASK_PAYLOAD)
        .typeError(TasksApiErrors.INVALID_SPLIT_BY_INVENTORY_PAYLOAD)
        .of(inventoryPayloadSchema)
        .min(
            REQUIRED_SPLIT_TASK_PAYLOAD_LENGTH,
            TasksApiErrors.INVALID_SPLIT_BY_INVENTORY_PAYLOAD
        )
        .max(
            REQUIRED_SPLIT_TASK_PAYLOAD_LENGTH,
            TasksApiErrors.INVALID_SPLIT_BY_INVENTORY_PAYLOAD
        )
});

export const cloneTasksForAssistantsSchema = yupObject({
    taskIds: yupArray()
        .required(TasksApiErrors.MISSING_TASKIDS)
        .typeError(TasksApiErrors.INVALID_TASKIDS)
        .of(
            yupString()
                .required(TasksApiErrors.MISSING_TASKIDS)
                .uuid(TasksApiErrors.INVALID_TASKIDS)
        )
        .min(1, TasksApiErrors.INVALID_TASKIDS)
});

export const getMetricsByDateSchema = yupObject({
    date: yupString()
        .required(TasksApiErrors.MISSING_DATE)
        .typeError(TasksApiErrors.INVALID_DATE)
        .matches(/^\d{4}-\d{2}-\d{2}$/, TasksApiErrors.INVALID_DATE)
});

export const getMetricsByDateRangeSchema = yupObject({
    startDate: yupString()
        .required(TasksApiErrors.MISSING_START_DATE)
        .typeError(TasksApiErrors.INVALID_START_DATE)
        .matches(/^\d{4}-\d{2}-\d{2}$/, TasksApiErrors.INVALID_START_DATE),
    endDate: yupString()
        .required(TasksApiErrors.MISSING_END_DATE)
        .typeError(TasksApiErrors.INVALID_END_DATE)
        .matches(/^\d{4}-\d{2}-\d{2}$/, TasksApiErrors.INVALID_END_DATE)
});
